import { BooleanInput, Box, PrimaryButton } from '@wrisk/ui-components'
import { TFunction } from 'i18next'
import { isNil } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import React from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { TKeyBuilder } from '../../../../infrastructure/internationalisation'
import {
  FormConfig,
  getFormats,
  InputLoopConfig,
  useConfig,
} from '../../../../state/configuration'
import { useDataWithDefaultValues } from '../../defaultValues'
import { getValidationRules } from '../../validation'
import { ErrorMessage } from '../ErrorMessage'
import { InputLoopForm } from './InputLoopForm'
import { InputLoopSummary } from './InputLoopSummary'

export * from './InputLoopMeta'

export interface InputLoopProps {
  item: InputLoopConfig
  config: FormConfig
  t: TFunction
  tKey: TKeyBuilder
  errorClassName?: string
}

export const InputLoop = ({ item, config, t, tKey, errorClassName }: InputLoopProps) => {
  const controllerName = `inputLoops-${item.name}`

  const formatsConfig = useConfig(getFormats)

  const { control, setValue } = useFormContext()

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: item.name,
  })

  const defaultValue = useDataWithDefaultValues(item.meta?.form ?? [], {})
  const actualValues = useWatch({
    control,
    name: item.name,
  })

  const [isAppending, setIsAppending] = useState(false)

  const handleRadioChange = useCallback(
    (onChange: (it: boolean) => void) => (value: boolean) => {
      value ? append(defaultValue) : remove()

      setIsAppending(value)
      onChange(value)
    },
    [append, item, remove],
  )

  const handleAdd = useCallback(() => {
    append(defaultValue)
    setIsAppending(true)
  }, [append, item])

  const handleCancel = useCallback(
    (index: number) => {
      remove(index)
      setIsAppending(false)
    },
    [remove],
  )

  const handleRemove = useCallback(
    (index: number) => {
      remove(index)
    },
    [remove],
  )

  const handleConfirm = useCallback(() => {
    move(fields.length - 1, fields.length - 1)
    setIsAppending(false)
  }, [])

  useEffect(() => {
    if (!fields.length) setValue(controllerName, false)
  }, [fields, setValue])

  return (
    <Box display='flex' flexDirection='column' gap={3} width={1}>
      <Controller
        control={control}
        defaultValue={isNil(actualValues) ? null : actualValues.length > 0}
        name={controllerName}
        render={({ field }) => (
          <BooleanInput
            name={field.name}
            onSelect={handleRadioChange(field.onChange)}
            value={field.value}
          />
        )}
        rules={{
          validate: getValidationRules(item.type, item.validation, config, formatsConfig),
        }}
      />

      {Boolean(fields.length) && (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
          width={1}
          gap={3}
        >
          {fields.map((field, index) => {
            const isLastElement = fields.length - 1 === index

            return isAppending && isLastElement ? (
              <InputLoopForm
                t={t}
                tKey={tKey}
                key={field.id}
                item={item}
                index={index}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                errorClassName={errorClassName}
              />
            ) : (
              <InputLoopSummary
                t={t}
                tKey={tKey}
                key={field.id}
                config={item}
                index={index}
                onRemove={handleRemove}
              />
            )
          })}

          {!isAppending && <PrimaryButton onClick={handleAdd}>Add another</PrimaryButton>}
        </Box>
      )}
      <ErrorMessage
        name={controllerName}
        tKey={tKey}
        t={t}
        tName={item.name}
        className={errorClassName}
      />
    </Box>
  )
}
